import { Tooltip } from 'recharts'
import { Payload, ValueType, NameType } from 'recharts/types/component/DefaultTooltipContent'

import { Column } from '../common/pure/FlexContainer'
import LineGraph, { LineGraphProps } from './LineGraph'
import Bolt from '../../models/Bolt'
import Vector2 from '../../models/Vector2'
import { kN, toDecimalPlaces } from '../../tools/format'
import { GRAPH_BORDER_STYLE } from './graph-constants'
import t from '../../tools/t'

interface PretensionGraphProps extends Omit<LineGraphProps, 'points' | 'onPointClick'> {
    bolts: Bolt[]
    onBoltClick?: (bolt: Bolt) => void
}

export default function PretensionGraph({ bolts, onBoltClick, ...props }: PretensionGraphProps) {
    // Map bolts to fixed indices instead of their assigned position.
    // Later, look up the assigned position based on this index.
    const points = bolts.map((b, i) => new Vector2(i, b.pretension))

    const getBoltFromTooltipPayload = (
        data: Array<Payload<ValueType, NameType>>
    ): Bolt | undefined => {
        const payload = data[0]?.payload as Vector2
        const index = payload?.x
        return typeof index === 'number' ? bolts[index] : undefined
    }

    const onPointClick = (_point: Vector2, index: number) => {
        if (!onBoltClick) return
        if (typeof index !== 'number') return
        const bolt = bolts[index]
        if (!bolt) return
        onBoltClick(bolt)
    }

    return (
        <LineGraph {...props} points={points} onPointClick={onPointClick} drawOobArea>
            <Tooltip
                isAnimationActive={false}
                position={{ y: 0 }}
                itemStyle={{ display: 'none' }}
                cursor={GRAPH_BORDER_STYLE}
                labelFormatter={(label, data) => (
                    <PretensionGraphTooltipFormat
                        label={label}
                        bolt={getBoltFromTooltipPayload(data)}
                    />
                )}
            />
        </LineGraph>
    )
}

interface PretensionGraphTooltipFormat {
    label: string
    bolt?: Bolt
}
const PretensionGraphTooltipFormat = ({ label, bolt }: PretensionGraphTooltipFormat) => (
    <Column className="graph__tooltip-format" tag="span">
        {typeof bolt?.pretension === 'number' && (
            <span>
                {t('common_pretension_force')}:{' '}
                <span className="graph__tooltip-format-value">
                    {kN(toDecimalPlaces(bolt.pretension))}
                </span>
            </span>
        )}
        <span>
            {t('common_index')}:{' '}
            <span className="graph__tooltip-format-value">
                {bolt?.assignment?.position ?? parseInt(label)}
            </span>
        </span>
    </Column>
)
